import el from "@cypherpotato/el";
import Temml from "temml";
import { getSession } from "../session";
import { displayAlert, displayFormControl } from "../ui/alert";
import { copyToClipboard } from "../ui/clipboard";
import { formatNumber } from "../ui/format";
import { renderMarkdown } from "../ui/md-renderer";
import { AudioRecorder } from "../ui/recorder";
import { cond, randomId, readBlob } from "../util";
import { Button, ButtonRow } from "./button";
import { Dropdown, DropdownItem } from "./dropdown";
import { TextArea } from "./form";
import { Icon } from "./icon";
import { Image } from "./image";
import { Warning } from "./warn";

export function ChatMessage({ role, initialMarkdown, initialReasoning, culture, id, options }) {

    const errorData = el.state.create(null);
    const reasoning = el.state.create(initialReasoning || "");
    const content = el.state.create(initialMarkdown || "");
    const linksData = el.state.create([]);

    const implicitThinkingLabel = el.state.create(null);
    const after = el.state.create(null);

    const messageCreatedAt = new Date();
    const reasoningCollapseAnimationMs = 280;
    let firstReasoningTokenReceived = false;
    let manualReasoningClicked = false;
    let finalAnswerStarted = false;
    let reasoningCollapseTimer;

    function rewriteLinks(self) {
        self.querySelectorAll(".chat-message-contents a").forEach(a => {
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
        });
    }

    async function decodeLinks() {
        const fetchOperation = options.onDecodeLink;

        if (!fetchOperation) {
            return [];
        }

        const matches = [...content.value.matchAll(/https:\/\/[\S]+/g)];
        const previews = [];
        const promises = [];

        for (const [url] of matches) {

            promises.push(fetchOperation(url).then(async response => {
                if (response.ok) {
                    const contentType = response.headers.get("Content-Type") || "";
                    if (contentType.includes("application/json+open-graph")) {
                        const data = await response.json();
                        previews.push(data);
                    } else if (contentType.includes("image/")) {
                        previews.push({ imageUrl: url, url });
                    }
                }
            }))
        }

        await Promise.all(promises);
        linksData.value = previews;

        return previews;
    }

    function normalizeThinkBlocks(text) {
        return text
            .replaceAll("<think>", "\n\n<think>\n\n")
            .replaceAll("</think>", "\n\n</think>\n\n");
    }

    function encodeAnswer(text) {
        return el.fragment(
            el(".answer", el.raw(renderMarkdown(normalizeThinkBlocks(text))))
        );
    }

    function encodeContent(content) {

        const messageContents = el.fragment();
        if (typeof content === "string") {
            if (role == "user") {
                if (content.startsWith("!#[conversation-attachment")) {
                    const filename = part.text.between('filename="', '"');
                    messageContents.append(el(".message-attachment.document-attachment",
                        Icon("file-4-line"),
                        el("span", filename)
                    ))
                } else {
                    messageContents.append(el("div", { style: { whiteSpace: "pre-wrap" } }, content.trim()));
                }
            } else {
                messageContents.append(encodeAnswer(content));
            }
        } else {
            for (const part of content) {
                if (!part || !part.type)
                    continue;

                if (part.type == "text" && part.text) {
                    if (role == "user") {
                        if (part.text.startsWith("!#[conversation-attachment")) {
                            const filename = part.text.between('filename="', '"');
                            messageContents.append(el(".message-attachment.document-attachment",
                                Icon("file-4-line"),
                                el("span", filename)
                            ))
                        } else {
                            messageContents.append(el("div", { style: { whiteSpace: "pre-wrap" } }, part.text.trim()));
                        }

                    } else {
                        messageContents.append(encodeAnswer(part.text));
                    }

                } else if (part.type == "image_url") {
                    messageContents.append(el(".message-attachment.image-attachment",
                        Image(part.image_url.url)
                    ))
                } else if (part.type == "input_audio") {
                    const audioSrc = part.input_audio.data.startsWith("data:")
                        ? part.input_audio.data
                        : "data:audio/" + part.input_audio.format + ";base64," + part.input_audio.data;

                    messageContents.append(el(".message-attachment.audio-attachment",
                        el("audio[controls]", { src: audioSrc })
                    ))
                }
            }
        }

        return messageContents;
    }

    return el("div.chat-message",
        {
            classList: ["chat-role-" + role],
            $functions: {
                append(chunk) {
                    if (this.classList.contains("has-implicit-thinking") &&
                        !this.classList.contains("answer-began")) {

                        const diffSeconds = Math.floor((new Date() - messageCreatedAt) / 1000);
                        implicitThinkingLabel.value = diffSeconds <= 3 ?
                            culture.stateThoughtFast :
                            culture.stateThought.replace("{0}", formatNumber(diffSeconds))

                        this.classList.add("answer-began");
                    }

                    if (!finalAnswerStarted && !manualReasoningClicked && firstReasoningTokenReceived) {
                        this.querySelector('.chat-message-parsed-reasoning details').open = false;
                    }

                    content.value += chunk;
                },
                appendReasoning(text, allowAutoOpen = true) {
                    reasoning.value += text;
                    implicitThinkingLabel.value = null;
                    this.classList.add("has-reasoning");
                    this.classList.remove("has-implicit-thinking");

                    if (!firstReasoningTokenReceived && allowAutoOpen) {
                        this.querySelector('.chat-message-parsed-reasoning details').open = true;
                        firstReasoningTokenReceived = true;
                    }
                },
                setContent(text) {
                    content.value = text;
                },
                beginFinalAnswer() {
                    if (finalAnswerStarted) {
                        return;
                    }

                    finalAnswerStarted = true;

                    if (content.value?.trim?.().length > 0) {
                        reasoning.value = [
                            reasoning.value,
                            content.value
                        ].filter(value => value?.trim?.().length > 0).join("\n\n");
                        content.value = "";
                    }

                    if (reasoning.value.trim().length > 0) {
                        const reasoningContainer = this.querySelector(".chat-message-parsed-reasoning");
                        const details = reasoningContainer.querySelector("details");
                        this.classList.add("has-reasoning", "answer-began");
                        this.classList.remove("has-implicit-thinking");
                        clearTimeout(reasoningCollapseTimer);
                        if (manualReasoningClicked) {
                            details.open = true;
                            return;
                        }
                        reasoningContainer.classList.add("reasoning-transitioning");
                        reasoningContainer.style.maxHeight = `${reasoningContainer.scrollHeight}px`;
                        reasoningContainer.style.opacity = "1";
                        reasoningContainer.style.transform = "translateY(0)";
                        details.classList.add("reasoning-collapse-locked");
                        details.open = true;

                        requestAnimationFrame(() => requestAnimationFrame(() => {
                            reasoningContainer.style.maxHeight = `${reasoningContainer.scrollHeight}px`;
                            requestAnimationFrame(() => {
                                reasoningContainer.style.maxHeight = `${details.querySelector("summary").offsetHeight}px`;
                                reasoningContainer.style.opacity = "1";
                                reasoningContainer.style.transform = "translateY(-2px)";
                            });

                            reasoningCollapseTimer = setTimeout(() => {
                                details.open = false;
                                requestAnimationFrame(() => {
                                    details.classList.remove("reasoning-collapse-locked");
                                    reasoningContainer.classList.remove("reasoning-transitioning");
                                    reasoningContainer.style.maxHeight = "";
                                    reasoningContainer.style.opacity = "";
                                    reasoningContainer.style.transform = "";
                                });
                            }, reasoningCollapseAnimationMs);
                        }));
                    }
                },
                setError(error) {
                    this.classList.add("error");
                    errorData.value = el("div",
                        el("div", error),
                        options?.onRetry && Button(culture.tryAgain, {
                            onClick() {
                                options.onRetry(this.closest('.chat-message'));
                            }
                        })
                    );
                },
                startImplicitThinking() {
                    this.classList.add("has-implicit-thinking");
                },
                setAfter(data) {
                    after.value = data;
                },
                setDone() {
                    this.classList.add("done");
                    if (role == "assistant") {
                        decodeLinks();
                    }

                    rewriteLinks(this);
                },
                setPlayingState(state) {
                    if (state) {
                        this.setAttribute("playing-audio", state);
                    } else {
                        this.removeAttribute("playing-audio");
                    }
                }
            },
            $properties: {
                messageId: {
                    get() {
                        return id;
                    }
                }
            },
            mount() {
                if (reasoning.value.trim().length > 0) {
                    this.classList.add("has-reasoning");
                }
                if (initialMarkdown && initialMarkdown.trim && initialMarkdown.trim().length > 0) {
                    this.classList.add("done");
                }
            }
        },
        el(".reasoning-block.implicit-thinking-block",
            el("summary.gradient-text", culture.stateThinking)
        ),
        el(".chat-message-parsed-reasoning.markdown-content",
            el("details.reasoning-block",
                el("summary.gradient-text",
                    {
                        onClick() {
                            manualReasoningClicked = true;
                        }
                    },
                    el("span", culture.reasoningShowButton), Icon("arrow-right-s-line")
                ),
                el.state.listening([reasoning], () =>
                    el(".reasoning-block-content",
                        el.raw(renderMarkdown(normalizeThinkBlocks(reasoning.value)))
                    )
                )
            )
        ),
        el.state.listening([content], () =>
            el(".chat-message-contents.markdown-content",
                encodeContent(content.value)
            )
        ),
        el.state.listening([after], () =>
            el("span",
                after.value
            )
        ),
        el.state.listening([errorData], () =>
            el("div.error",
                errorData.value && Warning(errorData.value)
            )
        ),
        el.state.listening([after, linksData], () =>
            el(".chat-message-after",
                el(".chat-message-preview-links",
                    linksData.value.map(ChatMessageMediaPreview)
                ),
                role == "assistant" && after.value == null && el(".chat-message-actions",
                    Button(
                        Icon("file-copy-line"),
                        {
                            onClick() {
                                copyToClipboard(content.value)
                            }
                        }
                    ),
                    options?.onRetry && Button(
                        Icon("reset-right-line"),
                        { classList: ["retry-button"] },
                        {
                            onClick() {
                                options.onRetry(this.closest('.chat-message'));
                            }
                        }
                    ),
                    options?.onSinthetize && el.fragment(
                        Button(
                            {
                                classList: ["start-play-button"]
                            },
                            Icon("volume-up-line"),
                            {
                                onClick() {
                                    options.onSinthetize({
                                        sender: this.closest('.chat-message'),
                                        state: "play",
                                        text: content.value
                                    });
                                }
                            }
                        ),
                        Button(
                            {
                                classList: ["stop-play-button"]
                            },
                            Icon("stop-fill"),
                            {
                                onClick() {
                                    options.onSinthetize({
                                        sender: this.closest('.chat-message'),
                                        state: "stop",
                                        text: ""
                                    });
                                }
                            }
                        )
                    ),
                    el(".loader")
                ) || el(".chat-message-actions")
            )
        )
    )
}

export function ChatMessageMediaPreview({ title, description, imageUrl, url }) {
    return el("a.chat-message-media-preview",
        {
            href: url,
            target: "_blank",
        },
        imageUrl && el("img.preview-image", { src: imageUrl, onError() { this.remove() } }),
        title && el(".preview-title", title),
        description && el(".preview-description", description),
    )
}

export function Chat({
    messageList,
    onMessage,
    onClear,
    onReturn,
    onSinthetize,
    onRetry,
    onDecodeLink,
    clientOptions,
    culture,
    disabledItemRow,
    extraItemsRow,
    presentation,
    header,
    props
}) {

    var canScrollToBottom = true;
    var lastScrollTop = 0;
    var auth = getSession();

    const attachments = el.state.create([]);
    const isRecordingAudio = el.state.create(false);

    function updateTextAreaHeight(textareaElement) {
        const maxHeight = 400;
        textareaElement.style.height = 'auto';
        const scrollHeight = Math.max(textareaElement.scrollHeight, 50);

        if (scrollHeight > maxHeight) {
            textareaElement.style.height = `${maxHeight}px`;
            textareaElement.style.overflowY = 'scroll';
        } else {
            textareaElement.style.height = `${scrollHeight}px`;
            textareaElement.style.overflowY = 'hidden';
        }
    }

    async function includeFileAsAttachment(file, type) {
        if (!file) {
            return;
        }
        if (!type) {
            if (file.type) {
                if (file.type.startsWith('image/')) {
                    type = 'image';
                } else if (file.type.startsWith('video/')) {
                    type = 'video';
                } else if (file.type.startsWith('audio/')) {
                    type = 'audio';
                }
            }

            if (!type) {
                type = 'file';
            }
        }

        const fileName = file.name;
        const reader = new FileReader();

        reader.onload = (e) => {
            const contentBase64 = e.target.result;
            var previewElement;

            switch (type) {
                case 'image':
                    previewElement = Image(contentBase64);
                    break;
                case 'video':
                    previewElement = Icon("video-on-line");
                    break;
                case 'audio':
                    previewElement = Icon("volume-up-line");
                    break;
                case 'file':
                    previewElement = Icon("attachment-line");
                    break;
                default:
                    console.error('Unknown attachment type:', type);
                    return;
            }

            attachments.value.push({
                type,
                name: fileName,
                contentBase64: contentBase64,
                mimeType: file.type,
                previewElement
            });
            attachments.requestUpdate();
        };

        reader.onerror = (error) => {
            console.error('Error reading file:', error);
        };

        reader.readAsDataURL(file);
    }

    async function openInlineDocumentEditor() {
        displayFormControl({
            title: culture.attachmentInlineDocumentTitle,
            formControlItem: TextArea(null, null, {
                rows: 10
            }),
            dialogSize: "large",
            onConfirm(value) {
                if (!value || value.trim().length === 0) {
                    return;
                }

                attachments.value.push({
                    type: "text",
                    name: "inline-document-" + randomId() + ".txt",
                    contentBase64: value,
                    previewElement: Icon("attachment-line")
                });
                attachments.requestUpdate();
            }
        })
    }

    async function openFilePicker({ type }) {
        const fileInput = el('input', { type: 'file' });

        switch (type) {
            case 'image':
                fileInput.accept = 'image/*';
                break;
            case 'video':
                fileInput.accept = 'video/*';
                break;
            case 'audio':
                fileInput.accept = 'audio/*';
                break;
            case 'document':
                fileInput.accept = '.txt,.doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx';
                break;
        }

        fileInput.addEventListener('change', (event) => {
            const file = event.target.files[0];
            includeFileAsAttachment(file);
        });

        fileInput.click();
    }

    const textArea = TextArea(null, null, {
        autocomplete: "false",
        autocorrect: "false",
        autocapitalize: "false",
        spellcheck: "false",
        placeholder: clientOptions?.textAreaPlaceholder?.nullWhenEmpty() || culture.inputPlaceholder,
        rows: 1,
        onKeyDown(event) {
            if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();

                onMessage(event.target.value, attachments.value);

                attachments.value = [];
                event.target.value = "";
                updateTextAreaHeight(event.target);
            }
        },
        onInput(event) {
            updateTextAreaHeight(event.target);
        },
        onPaste(event) {
            const items = event.clipboardData?.items;
            if (!items) return;

            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                if (item.kind === 'file') {
                    event.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        includeFileAsAttachment(file);
                    }
                }
            }
        },
        mount() {
            updateTextAreaHeight(this);
        }
    });

    document.addEventListener("scroll", function () {
        let scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        if (scrollTop < lastScrollTop) {
            canScrollToBottom = false;
        } else {
            canScrollToBottom = true;
        }
        lastScrollTop = scrollTop;

    }, false);

    const audioRecorder = AudioRecorder({
        recordingStarted: () => {
            ;
        },
        recordingStopped: async (audioBlob) => {
            const bob = await readBlob(audioBlob);
            attachments.value.push({
                type: "audio",
                name: "audio.wav",
                contentBase64: bob,
                mimeType: "audio/wav",
                format: "wav",
                previewElement: Icon("volume-up-line")
            });
            onMessage(null, attachments.value);
            attachments.value = [];
        }
    })

    return el(".chat-wrapper",
        header && el(".chat-header",
            el(".chat-header-inner",
                el(".chat-actions",
                    onReturn && el(".chat-action",
                        Icon("list-check"),
                        {
                            onClick() { onReturn.call() }
                        }
                    ),
                    onClear && el(".chat-action",
                        Icon("edit-box-line"),
                        {
                            onClick() { onClear.call() }
                        }
                    )
                ),
                el(".chat-title",
                    header.title
                ),
                el(".chat-auth",
                    auth.data && el.fragment(
                        el(".account-avatar",
                            el("img", { src: `https://0.gravatar.com/avatar/${auth.data.emailSha256}?size=64` }),
                            {
                                onClick() {
                                    window.open('/dashboard', '_blank')
                                }
                            }
                        ),
                    )
                )
            )
        ),
        presentation && el(".chat-presentation",
            presentation.logoSrc && el(".chat-presentation-logo",
                Image(presentation.logoSrc)
            ),
            el(".chat-presentation-title",
                presentation.helloTitle || culture.helloLabel
            ),
            el(".chat-presentation-sub-title",
                presentation.helloSubtitle || culture.helloSubLabel
            )
        ),
        ChatMessages(
            messageList
                .filter(msg => msg && msg.role && msg.content?.length)
                .reduce((messages, msg) => {
                    const lastMessage = messages.at(-1);
                    const isWorkMessage = msg.role == "reasoning" || msg.role == "hidden" || msg.role == "verbose";

                    if (msg.role == "assistant") {
                        if (lastMessage?.role == "assistant") {
                            messages[messages.length - 1] = {
                                ...msg,
                                workContent: [
                                    lastMessage.workContent,
                                    typeof lastMessage.content == "string" && lastMessage.content
                                ].filter(value => value?.trim?.().length > 0).join("\n\n")
                            };
                        } else if (lastMessage?.role == "reasoning") {
                            messages.pop();
                            messages.push({ ...msg, workContent: lastMessage.content });
                        } else {
                            messages.push(msg);
                        }
                    } else if (isWorkMessage) {

                        if (msg.role == "reasoning") {
                            msg.content = `<think>\n\n${msg.content.trim()}\n\n</think>`
                        }

                        if (lastMessage?.role == "assistant") {

                            lastMessage.workContent = [
                                lastMessage.workContent,
                                typeof msg.content == "string" && msg.content
                            ].filter(value => value?.trim?.().length > 0).join("\n\n");

                        } else if (lastMessage?.role == "reasoning") {
                            lastMessage.content += "\n\n" + msg.content;

                        } else {
                            messages.push({ ...msg, role: "reasoning" });
                        }
                    } else {
                        messages.push(msg);
                    }

                    return messages;
                }, [])
                .map(msg => {
                    if (msg.role == "reasoning") {
                        var message = ChatMessage({
                            role: msg.role,
                            initialMarkdown: "",
                            initialReasoning: msg.content,
                            culture,
                            options: {
                                onSinthetize,
                                onDecodeLink,
                                onRetry
                            }
                        });
                        message.setDone();
                        return message;
                    } else {
                        var message = ChatMessage({
                            role: msg.role,
                            initialMarkdown: msg.content,
                            initialReasoning: msg.workContent,
                            culture,
                            options: {
                                onSinthetize,
                                onDecodeLink,
                                onRetry
                            }
                        });
                        message.setDone();
                        return message;
                    }
                })
        ),
        el(".chat-input-container",
            clientOptions?.suggestionButtons.length > 0 && el(".suggestion-button-row",
                clientOptions.suggestionButtons.map(button =>
                    Button(button.label, {
                        onClick() {
                            onMessage(button.prompt, []);
                        }
                    })
                )
            ),
            extraItemsRow && el(".extra-item-row",
                extraItemsRow
            ),
            disabledItemRow && el(".disabled-item-row",
                disabledItemRow
            ),
            el.state.listening([attachments], () =>
                el(".attachments-row",
                    attachments.value.map(attch =>
                        el(".attachment-item",
                            el("span", attch.previewElement),
                            el("span", attch.name),
                            el("span", Icon("close-line"), {
                                onClick() {
                                    attachments.value = attachments.value.filter(a => a !== attch);
                                }
                            })
                        )
                    )
                )
            ),
            el(".chat-input-wrapper",
                textArea,
                el(".input-actions",
                    ButtonRow(
                        clientOptions?.inputModes.length > 0 && Dropdown(
                            Icon("attachment-line"),
                            ...clientOptions.inputModes.map(mode =>
                                cond(
                                    [mode == "Document", el.fragment(
                                        DropdownItem(culture.attachmentDocumentTitle, Icon("file-2-line"), {
                                            onClick() {
                                                openFilePicker({ type: "document" });
                                            }
                                        }),
                                        DropdownItem(culture.attachmentInlineDocumentTitle, Icon("align-left"), {
                                            onClick() {
                                                openInlineDocumentEditor();
                                            }
                                        })
                                    )],
                                    [mode == "Image", DropdownItem(culture.attachmentImageTitle, Icon("image-line"), {
                                        onClick() {
                                            openFilePicker({ type: "image" });
                                        }
                                    })],
                                )
                            )
                        )
                    ),
                    ButtonRow(
                        clientOptions?.inputModes.length > 0 &&
                        clientOptions.inputModes.includes("Audio")
                        && el.state.listening([isRecordingAudio], () =>
                            Button(
                                {
                                    classList: ["record-button", isRecordingAudio.value && "recording"]
                                },
                                isRecordingAudio.value ? Icon("record-circle-fill") : Icon("record-circle-line"),
                                {
                                    async onClick(event) {
                                        try {
                                            if (isRecordingAudio.value) {
                                                audioRecorder.stopRecording();
                                                isRecordingAudio.value = false;
                                            } else {
                                                await audioRecorder.startRecording();
                                                isRecordingAudio.value = true;
                                            }
                                        } catch (error) {
                                            isRecordingAudio.value = false;
                                            displayAlert({
                                                message: error.message
                                            });
                                        }
                                    }
                                }
                            )
                        ),
                        Button(
                            {
                                classList: "send-button"
                            },
                            Icon("arrow-up-line"),
                            {
                                onClick() {
                                    const value = textArea.value.trim();
                                    if (value.length > 0) {
                                        onMessage(value, attachments.value);

                                        attachments.value = [];
                                        textArea.value = "";
                                        updateTextAreaHeight(textArea);
                                    }
                                }
                            }
                        )
                    )
                ),
                {
                    onDrop(event) {
                        event.preventDefault();
                    }
                }
            )
        ),
        {
            $properties: {
                isReadOnly: {
                    get() {
                        return this.querySelector(".chat-input-container").classList.contains("disabled");
                    },
                    set(value) {
                        this.querySelector(".chat-input-container").classList.toggle("disabled", value);
                    }
                }
            },
            $functions: {
                addFile(file) {
                    includeFileAsAttachment(file)
                },
                createMessage(role, text) {
                    const newMessage = ChatMessage({
                        role,
                        initialMarkdown: text || "",
                        culture,
                        options: {
                            onSinthetize,
                            onRetry
                        }
                    });

                    this.querySelector(".chat-messages").appendChild(newMessage);
                    return newMessage;
                },
                renderCodeBlocks() {
                    Prism.highlightAll();

                    for (const pre of this.querySelectorAll('.chat-messages pre[class*="language-"]')) {

                        if (pre.querySelector(".code-toolbar") == null) {
                            const toolbar = el(".code-toolbar",
                                Button(
                                    Icon("file-copy-line"),
                                    culture.copyButtonLabel,
                                    {
                                        onClick() {
                                            copyToClipboard(pre.querySelector("code").textContent);
                                        }
                                    }
                                )
                            )
                            pre.insertBefore(toolbar, pre.firstChild)
                        }
                    }

                    window.temml = Temml;
                    for (const math of this.querySelectorAll('.chat-messages span.math')) {
                        const mathExpr = Temml.renderToString(math.textContent);
                        math.innerHTML = mathExpr;
                    }

                    for (const iframe of this.querySelectorAll('.chat-messages iframe')) {
                        if (iframe.parentNode.classList.contains("iframe-wrapper")) {
                            continue;
                        }
                        const wrapper = el(".iframe-wrapper",
                            el(".iframe-toolbar",
                                ButtonRow(
                                    Button(
                                        Icon("file-copy-line"),
                                        culture.copyLinkButtonLabel,
                                        {
                                            onClick() {
                                                copyToClipboard(iframe.src);
                                            }
                                        }
                                    ),
                                    Button(
                                        Icon("external-link-line"),
                                        culture.openInNewTab,
                                        {
                                            onClick() {
                                                window.open(iframe.src, '_blank');
                                            }
                                        }
                                    ),
                                    Button(
                                        Icon("fullscreen-line"),
                                        {
                                            onClick() {
                                                displayAlert({
                                                    title: "Generated content",
                                                    dialogSize: "lightbox",
                                                    message: [
                                                        iframe.cloneNode(true)
                                                    ]
                                                });
                                            }
                                        }
                                    )
                                )
                            )
                        );
                        iframe.removeAttribute('style');
                        iframe.parentNode.insertBefore(wrapper, iframe);
                        wrapper.appendChild(iframe);
                    }
                },
                addLightboxes() {
                    for (const img of this.querySelectorAll(".chat-messages img")) {
                        if (img.classList.contains(".lightbox")) {
                            continue;
                        }

                        img.addEventListener("click", () => {
                            displayAlert({
                                title: "Generated image",
                                dialogSize: "lightbox",
                                message: [
                                    el("img", {
                                        src: img.src
                                    })
                                ]
                            });
                        });
                        img.classList.add("lightbox");
                    }
                },
                scrollToBottom(force) {
                    if (canScrollToBottom || force)
                        window.scrollTo({ top: document.body.scrollHeight, behavior: "instant" });;
                }
            },
            mount() {
                this.renderCodeBlocks();
                this.addLightboxes();
                setTimeout(() => {
                    this.scrollToBottom(true);
                }, 300);
            }
        },
        props
    );
}

export function ChatMessages(...children) {
    return el("div.chat-messages",
        ...children
    )
}
